import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { RootState } from '../redux/store';
import { tokenReceived, logout } from '../features/auth/authSlice';
import { refrescarSesion } from './refrescarSesion';
import { settings } from '../settings';

export interface GetAuditorBandejaParams {
    estadoGestion?: string;
    skip?: number;
    take?: number;
}

export interface GetPeriodicosEmployeesParams {
    lote?: string;
    empleado?: string;
    idOrganizacion?: number;
    idOrganismoCentral?: number;
    idOrganismo?: number;
    idEstablecimiento?: number;
    idPrestador?: number | string;
    asistencia?: string;
    estadoGestion?: string;
    citaciones?: number;
    fechaTurno?: string;
    riesgos?: string;
    estadoExamenes?: string;
    skip?: number;
    take?: number;
}

const baseQuery = fetchBaseQuery({
    baseUrl: `${settings.url.host}${settings.url.api}`,
    credentials: 'include',
    prepareHeaders: (headers, { getState }) => {
        const accessToken = (getState() as RootState).auth.accessToken;
        if (accessToken) {
            headers.set('authorization', `Bearer ${accessToken}`);
        }
        return headers;
    }
});

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
    args,
    api,
    extraOptions
) => {
    let result = await baseQuery(args, api, extraOptions);
    if (result.error && result.error.status === 401) {
        const refreshResult = await refrescarSesion(baseQuery, api, extraOptions);
        if (refreshResult?.data) {
            api.dispatch(tokenReceived(refreshResult.data));
            result = await baseQuery(args, api, extraOptions);
        } else {
            api.dispatch(logout());
        }
    }
    return result;
};

export const periodicosEmployeeApi = createApi({
    reducerPath: 'periodicosEmployeeApi',
    baseQuery: baseQueryWithReauth,
    tagTypes: ['PeriodicosEmployees'],
    endpoints: (builder) => ({
        getPeriodicosEmployees: builder.query<any, GetPeriodicosEmployeesParams>({
            query: (params) => ({
                url: `periodicos/lotes/empleados`,
                params: { ...params }
            }),
            providesTags: ['PeriodicosEmployees']
        }),
        getAuditorBandeja: builder.query<any, GetAuditorBandejaParams>({
            query: (params) => ({
                url: `periodicos/empleados/bandeja-auditor`,
                params: { ...params }
            }),
            providesTags: ['PeriodicosEmployees']
        })
    })
});

export const {
    useLazyGetPeriodicosEmployeesQuery,
    useLazyGetAuditorBandejaQuery
} = periodicosEmployeeApi;
